import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiTruck, FiShield, FiRefreshCw, FiArrowRight } from 'react-icons/fi';
import ProductCard from '../components/ProductCard';
import Loader from '../components/Loader';
import API from '../services/api';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await API.get('/products');
        const featured = res.data.filter(p => p.featured);
        setFeaturedProducts(featured);
      } catch (error) {
        console.error('Erreur chargement produits:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  if (loading) return <Loader />;

  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-blue-800 text-white py-24 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12">
          <div className="flex-1">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-6">
              La Mode Africaine <br />
              <span className="text-secondary">au Meilleur Prix</span>
            </h1>
            <p className="text-gray-200 text-lg mb-8 leading-relaxed">
              Découvrez notre collection exclusive de boubous, bazin, wax et tenues traditionnelles sénégalaises. Livraison partout au Sénégal.
            </p>
            <div className="flex gap-4 flex-wrap">
              <Link
                to="/catalogue"
                className="bg-secondary text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-orange-600 transition flex items-center gap-2"
              >
                Voir le catalogue <FiArrowRight size={20} />
              </Link>
              <Link
                to="/register"
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-primary transition"
              >
                S'inscrire
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: <FiTruck size={32} />, title: 'Livraison rapide', desc: 'Partout au Sénégal en 24-48h' },
            { icon: <FiShield size={32} />, title: 'Paiement sécurisé', desc: 'Wave, Orange Money, Free Money' },
            { icon: <FiRefreshCw size={32} />, title: 'Retours faciles', desc: 'Satisfait ou remboursé sous 7 jours' },
          ].map((item, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 flex items-center gap-4 shadow-sm">
              <div className="text-primary">{item.icon}</div>
              <div>
                <h3 className="font-bold text-gray-800">{item.title}</h3>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            Nos <span className="text-primary">Catégories</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: 'Homme', bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
              { name: 'Femme', bg: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-800' },
              { name: 'Enfant', bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-800' },
            ].map((cat) => (
              <Link
                key={cat.name}
                to={`/catalogue?category=${cat.name}`}
                className={`${cat.bg} ${cat.border} border-2 rounded-2xl p-8 text-center hover:shadow-lg transition group`}
              >
                <h3 className={`text-2xl font-bold ${cat.text}`}>{cat.name}</h3>
                <p className="text-gray-500 text-sm mt-2">Voir la collection</p>
                <div className={`mt-4 inline-flex items-center gap-1 ${cat.text} font-semibold text-sm`}>
                  Explorer <FiArrowRight size={16} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Produits en vedette */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <h2 className="text-3xl font-bold text-gray-800">
              Produits <span className="text-primary">Populaires</span>
            </h2>
            <Link
              to="/catalogue"
              className="text-primary font-semibold hover:underline flex items-center gap-1"
            >
              Voir tout <FiArrowRight size={16} />
            </Link>
          </div>
          {featuredProducts.length === 0 ? (
            <p className="text-center text-gray-500">Aucun produit disponible pour le moment.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Banner CTA */}
      <section className="bg-secondary py-16 px-4 text-white text-center">
        <h2 className="text-3xl font-bold mb-4">
          Livraison rapide partout au Sénégal
        </h2>
        <p className="text-lg mb-8 text-orange-100">
          Payez avec Wave, Orange Money ou Free Money en toute sécurité
        </p>
        <Link
          to="/catalogue"
          className="bg-white text-secondary px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition inline-flex items-center gap-2"
        >
          Commander maintenant <FiArrowRight size={20} />
        </Link>
      </section>
    </div>
  );
};

export default Home;